/**
 * MobileSnapCarousel — horizontal snap-x scroller with subtle dot indicator.
 * Designed for use only on mobile (parent should add `md:hidden`).
 * Each direct child is wrapped in a snap-aligned, shrink-0 slot.
 */
import React, { useEffect, useRef, useState } from 'react';

export default function MobileSnapCarousel({ children, slotWidth = '78vw', gap = '1rem', testid }) {
  const scrollerRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const items = React.Children.toArray(children);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const children = Array.from(el.children);
        if (children.length === 0) return;
        const scrollerCenter = el.scrollLeft + el.clientWidth / 2;
        let best = 0;
        let bestDist = Infinity;
        children.forEach((c, i) => {
          const center = c.offsetLeft + c.clientWidth / 2;
          const d = Math.abs(center - scrollerCenter);
          if (d < bestDist) { bestDist = d; best = i; }
        });
        setActiveIdx(best);
      });
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => { el.removeEventListener('scroll', onScroll); cancelAnimationFrame(raf); };
  }, [items.length]);

  function scrollTo(i) {
    const el = scrollerRef.current;
    if (!el) return;
    const child = el.children[i];
    if (!child) return;
    el.scrollTo({ left: child.offsetLeft - 24, behavior: 'smooth' });
  }

  return (
    <div data-testid={testid}>
      <div
        ref={scrollerRef}
        className="flex overflow-x-auto snap-x snap-mandatory -mx-6 px-6 pb-2 scrollbar-hide"
        style={{ gap, scrollPaddingLeft: '1.5rem', scrollPaddingRight: '1.5rem', WebkitOverflowScrolling: 'touch' }}
      >
        {items.map((child, i) => (
          <div key={i} className="snap-start shrink-0" style={{ width: slotWidth }}>
            {child}
          </div>
        ))}
        {/* Tail spacer so the last card can snap to the left edge */}
        <div className="shrink-0 w-6" aria-hidden />
      </div>
      {items.length > 1 && (
        <div className="flex justify-center gap-1.5 mt-4" role="tablist" aria-label="carousel progress">
          {items.map((_, i) => (
            <button
              key={i}
              onClick={() => scrollTo(i)}
              aria-label={`Go to slide ${i + 1}`}
              data-testid={`${testid}-dot-${i}`}
              className={`h-1.5 rounded-full transition-all ${i === activeIdx ? 'w-6 bg-charcoal' : 'w-1.5 bg-charcoal/25'}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
